import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Trash2, Truck, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"
import { DeleteDistributorComposer } from "@/forms/sensory"
import { useToast } from "@/contexts/ToastContext"

export interface Distributor {
  id: string
  distributorNumber: number
  distributorName: string
}

interface DistributorListProps {
  onAdd?: () => void
  onEdit?: (distributor: Distributor) => void
}

export function DistributorList({ onAdd, onEdit }: DistributorListProps) {
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null)
  const itemsPerPage = 10

  // Fetch distributors from API
  const { data, isLoading, isError, refetch } = useApiQuery(
    "getDeviceDistributors",
    {}
  )

  // Map API response to component state, excluding archived items
  const distributors: Distributor[] = (data?.content?.distributors || [])
    .filter((d: any) => !d.isArchived)
    .map((d: any) => ({
      id: d.id || String(d.distributorNumber),
      distributorNumber: d.distributorNumber,
      distributorName: d.distributorName,
    }))

  const filteredDistributors = distributors.filter((distributor) =>
    distributor.distributorName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const totalPages = Math.ceil(filteredDistributors.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedDistributors = filteredDistributors.slice(startIndex, startIndex + itemsPerPage)

  const handleDelete = (distributor: Distributor) => {
    setSelectedDistributor(distributor)
    setIsDeleteDialogOpen(true)
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load distributors</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Truck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Distributors</CardTitle>
                <CardDescription className="text-base">Manage device distributors</CardDescription>
              </div>
            </div>

            <Button size="sm" className="gap-2" onClick={() => onAdd?.()}>
              <Plus className="h-4 w-4" />
              Add Distributor
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search distributors..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
        {/* Table */}
        <Table className="text-base">
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-[100px] font-semibold text-base">ID</TableHead>
              <TableHead className="font-semibold text-base">Distributor Name</TableHead>
              <TableHead className="w-[120px] text-right font-semibold text-base">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedDistributors.length > 0 ? (
              paginatedDistributors.map((distributor) => (
                <TableRow key={distributor.id} className="transition-colors hover:bg-muted/50">
                  <TableCell className="text-base text-muted-foreground">
                    {distributor.distributorNumber}
                  </TableCell>
                  <TableCell className="font-medium text-base">
                    <Link
                      to="/sensory/distributors/$distributorNumber"
                      params={{ distributorNumber: String(distributor.distributorNumber) }}
                      className="text-primary hover:underline"
                    >
                      {distributor.distributorName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                        <Link
                          to="/sensory/distributors/$distributorNumber/edit"
                          params={{ distributorNumber: String(distributor.distributorNumber) }}
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit {distributor.distributorName}</span>
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleDelete(distributor)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete {distributor.distributorName}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                  No distributors found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t py-3">
            <p className="text-base text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + itemsPerPage, filteredDistributors.length)} of{" "}
              {filteredDistributors.length} distributors
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-base font-medium">Page {currentPage} of {totalPages}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Distributor</DialogTitle>
            <DialogDescription>
              Please confirm you want to delete this distributor.
            </DialogDescription>
          </DialogHeader>
          {selectedDistributor && (
            <DeleteDistributorComposer
              distributorNumber={selectedDistributor.distributorNumber}
              distributorName={selectedDistributor.distributorName}
              showHeader={false}
              onSuccess={() => {
                setIsDeleteDialogOpen(false)
                setSelectedDistributor(null)
                refetch()
              }}
              onError={(error) => {
                toast({ title: 'Error', description: `Failed to delete distributor: ${error.message}`, type: 'error' })
              }}
              onCancel={() => {
                setIsDeleteDialogOpen(false)
                setSelectedDistributor(null)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
